// ui-notify.js
import Swal from "sweetalert2";

const btn = {
  primary: "btn btn-primary",
  danger: "btn btn-danger",
  secondary: "btn btn-secondary",
  info: "btn btn-info",
  warning: "btn btn-warning",
};

const baseModal = {
  buttonsStyling: false,
  showCloseButton: false,
  confirmButtonText: "확인",
  cancelButtonText: "취소",
  customClass: {
    confirmButton: `${btn.primary}`,
    cancelButton: btn.secondary,
  },
};

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  showCloseButton: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

function normalizeTextArgs(titleOrMessage, options = {}) {
  // 편의: 문자열 하나만 주면 text로 처리
  if (typeof titleOrMessage === "string") {
    return { text: titleOrMessage, ...options };
  }
  return { ...titleOrMessage };
}

const uiNotify = {
  toast: {
    success(message, options) {
      return Toast.fire({ icon: "success", ...normalizeTextArgs(message, options) });
    },
    error(message, options) {
      return Toast.fire({ icon: "error", ...normalizeTextArgs(message, options), timer: 5000 });
    },
    info(message, options) {
      return Toast.fire({ icon: "info", ...normalizeTextArgs(message, options) });
    },
    warning(message, options) {
      return Toast.fire({ icon: "warning", ...normalizeTextArgs(message, options), timer: 5000 });
    },
  },

  alert: {
    success(message, options) {
      return Swal.fire({ ...baseModal, icon: "success", ...normalizeTextArgs(message, options) });
    },
    error(message, options) {
      // 에러는 자동 닫힘 없음(기본), 사용자가 확인하도록
      return Swal.fire({ ...baseModal, icon: "error", ...normalizeTextArgs(message, options) });
    },
    info(message, options) {
      return Swal.fire({ ...baseModal, icon: "info", ...normalizeTextArgs(message, options) });
    },
    warning(message, options) {
      return Swal.fire({ ...baseModal, icon: "warning", ...normalizeTextArgs(message, options) });
    },
  },

  confirm: {
    danger(message, options = {}) {
      return Swal.fire({
        ...baseModal,
        icon: "warning",
        showCancelButton: true,
        customClass: {
          ...baseModal.customClass,
          confirmButton: `${btn.danger} me-2`,
          cancelButton: btn.secondary,
        },
        ...normalizeTextArgs(message, options),
      });
    },

    warning(message, options = {}) {
      return Swal.fire({
        ...baseModal,
        icon: "warning",
        showCancelButton: true,
        customClass: {
          ...baseModal.customClass,
          confirmButton: `${btn.primary} me-2`,
          cancelButton: btn.secondary,
        },
        ...normalizeTextArgs(message, options),
      });
    },

    basic(message, options = {}) {
      return Swal.fire({
        ...baseModal,
        icon: "question",
        showCancelButton: true,
        customClass: {
          ...baseModal.customClass,
          confirmButton: `${btn.primary} me-2`,
          cancelButton: btn.secondary,
        },
        ...normalizeTextArgs(message, options),
      });
    },
  },
};

// =========================
// PHP Notify payload dispatch
// =========================
function dispatch(payload) {
  if (!payload) return;

  // channel/type 둘 다 허용
  const channel = (payload.channel ?? payload.type ?? "toast").toString();

  // icon/variant/color 모두 허용
  const iconRaw = (payload.icon ?? payload.variant ?? payload.color ?? "info").toString();
  const icon = ["success", "error", "warning", "info", "question"].includes(iconRaw) ? iconRaw : "info";

  const title = payload.title ?? undefined;
  const text = payload.text ?? payload.msg ?? payload.message ?? undefined;
  const html = payload.html ?? undefined;

  // 공통 옵션
  const baseOptions = {
    icon,
    title,
    text,
    html,
  };

  // toast 옵션
  const timer = payload.timer != null ? parseInt(payload.timer, 10) : undefined;
  const position = payload.position ? String(payload.position) : undefined;

  // confirm 옵션
  const confirmStyle = (payload.confirmStyle ?? payload.style ?? "warning").toString(); // danger|warning|basic
  const confirmText = payload.confirmText ?? payload.confirmButtonText ?? baseModal.confirmButtonText ?? "확인";
  const cancelText = payload.cancelText ?? payload.cancelButtonText ?? baseModal.cancelButtonText ?? "취소";
  const actionUrl = payload.actionUrl ?? payload.redirect ?? null;

  if (channel === "toast") {
    const fn = uiNotify.toast[icon] ?? uiNotify.toast.info;

    // Toast.fire에서 position/timer 오버라이드 가능
    const opts = {
      ...normalizeTextArgs(text ?? title ?? "", { title, html }),
      ...(Number.isFinite(timer) ? { timer } : {}),
      ...(position ? { position } : {}),
    };

    return fn(text ?? title ?? "", opts);
  }

  if (channel === "alert") {
    const fn = uiNotify.alert[icon] ?? uiNotify.alert.info;
    return fn({
      title,
      text,
      html,
      // 필요하면 showCloseButton 같은 것도 payload로 열어둘 수 있음
      showCloseButton: payload.showCloseButton ?? baseModal.showCloseButton,
    });
  }

  if (channel === "confirm") {
    const method =
      confirmStyle === "danger" ? "danger" :
      confirmStyle === "basic" ? "basic" :
      "warning";

    const fn = uiNotify.confirm[method] ?? uiNotify.confirm.warning;

    return fn({
      title: title ?? "확인",
      text: text ?? "",
      html,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
    }).then((result) => {
      if (result.isConfirmed && actionUrl) {
        window.location.href = actionUrl;
      }
      return result;
    });
  }

  // 알 수 없는 채널이면 안전하게 toast info
  return uiNotify.toast.info(text ?? title ?? "");
}

// ui.notify.dispatch 로 노출
uiNotify.dispatch = dispatch;

// 페이지에 window.__notify 가 있으면 자동 실행
(function autoRunNotify() {
  const run = () => {
    if (window.__notify && window.ui?.notify?.dispatch) {
      window.ui.notify.dispatch(window.__notify);
      try { delete window.__notify; } catch (_) { window.__notify = null; }
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();

// 전역 한 곳만
window.ui = window.ui || {};
window.ui.notify = uiNotify;

window.ui.toast = uiNotify.toast;
window.ui.alert = uiNotify.alert;
window.ui.confirm = uiNotify.confirm;

// 기존 브라우저 alert / confirm 백업
window.__nativeAlert = window.alert;
window.__nativeConfirm = window.confirm;

// alert override
window.alert = function (message) {
  return uiNotify.alert.info(message);
};

// confirm override
window.confirm = function (message) {
  return uiNotify.confirm.basic(message).then(result => result.isConfirmed);
};